import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Card from 'reactstrap/lib/Card';
import Button from 'reactstrap/lib/Button';
import { ALL_VALUE } from 'site-modules/shared/constants/incentives/electric-car-incentives';
/* Utils */
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { generateSrpLinkNewType, srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getStaticImageUrl } from 'client/utils/image-helpers';
/* Components */
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { Link } from 'site-modules/shared/components/link/link';
import { PlaceholderCard } from 'site-modules/shared/components/incentives/placeholder-card/placeholder-card';
import { IncentiveBadge } from 'site-modules/shared/components/incentives/incentive-badge/incentive-badge';
import { IncentivesCardListOptions } from 'site-modules/shared/components/incentives/card-list-options/incentives-card-list-options';
import { PROGRAM_TYPES } from 'site-modules/shared/constants/incentives';

import './incentive-card.scss';

function getSrpLink({ makeSlug, modelSlug }) {
  return makeSlug
    ? srpLinkBuilder({
        make: makeSlug,
        model: modelSlug,
        engineType: modelSlug ? undefined : 'electric',
        inventorytype: 'new',
      })
    : generateSrpLinkNewType('electric');
}

const programIconMap = {
  [PROGRAM_TYPES.EV_CHARGING]: { src: 'icons/vertical-ev-battery.svg', alt: 'Battery' },
  [PROGRAM_TYPES.CHARGER_INSTALLATION]: { src: 'icons/plug.svg', alt: 'Plug' },
};

export const IncentiveCard = memo(
  ({
    openIncentivesModal,
    makeSlug = '',
    modelSlug = '',
    subtype = '',
    rebateAmount = 0,
    programType = '',
    endDate = '',
    restrictions = '',
    showPlaceholder = false,
    id,
    grantor = '',
  }) => {
    const isDefaultValue = value => value === ALL_VALUE;

    const listItems = [
      { label: 'Program Provider', value: grantor || '' },
      { label: 'Expiration Date', value: endDate },
    ];

    return (
      <Card className="incentive-card h-100 p-1 overflow-hidden border-0 justify-content-between">
        <div>
          <IncentiveBadge
            programType={programType}
            badgeContent={programType}
            showPlaceholder={showPlaceholder}
            rootClassName="mb-0_5"
          />
          <div className="incentive-card-header mb-1">
            <h2 className="size-16 mb-0 text-cool-gray-10 truncate-2" title={subtype}>
              {subtype}
            </h2>
          </div>

          <div className="py-2 pos-r mb-1">
            {!!rebateAmount && (
              <div className="pos-a center-xy text-center">
                <div className="size-36 fw-bold text-cool-gray-30 mb-0_25">{getPriceString(rebateAmount)}</div>
                <div className="medium fw-medium text-cool-gray-40">Maximum Rebate</div>
              </div>
            )}
            {!rebateAmount && programType in programIconMap && (
              <img
                className="pos-a center-xy"
                src={getStaticImageUrl(programIconMap[programType].src)}
                alt={programIconMap[programType].alt}
                loading="lazy"
              />
            )}
          </div>

          <ContentFragment componentToUse="div" classes="size-14 text-cool-gray-40 truncate-3 mb-1">
            {restrictions}
          </ContentFragment>

          <IncentivesCardListOptions listItems={listItems} />
        </div>

        <div>
          <Button
            color="link"
            size="lg"
            className="view-incentives-button text-cool-gray-40 size-16 p-0 text-transform-none fw-normal text-underline mb-1"
            onClick={openIncentivesModal}
            data-modalid={id}
            data-tracking-id="display_incentive_details"
            data-tracking-value={programType}
          >
            View More Details
            <span className="visually-hidden"> for {subtype}</span>
          </Button>

          <Button
            tag={Link}
            color="blue-50"
            size="lg"
            outline
            className="view-inventory-btn size-16 w-100 px-0_25 py-0_5 text-transform-none pos-r"
            to={getSrpLink({
              makeSlug: isDefaultValue(makeSlug) ? '' : makeSlug,
              modelSlug: isDefaultValue(modelSlug) ? '' : modelSlug,
            })}
            data-tracking-id="view_search_result"
          >
            Shop EV Inventory
          </Button>
        </div>
        {showPlaceholder && <PlaceholderCard />}
      </Card>
    );
  }
);

IncentiveCard.displayName = 'IncentiveCard';

IncentiveCard.propTypes = {
  openIncentivesModal: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  makeSlug: PropTypes.string,
  modelSlug: PropTypes.string,
  subtype: PropTypes.string,
  rebateAmount: PropTypes.number,
  programType: PropTypes.string,
  endDate: PropTypes.string,
  restrictions: PropTypes.string,
  showPlaceholder: PropTypes.bool,
  grantor: PropTypes.string,
};
